/**
 * Switch — Unity (bundle discount %) ↔ live-price bridge (Kaching Bundles aware).
 *
 * The Unity badge (blocks/switch-unit-price.liquid) renders a server-side default
 * discount % (product price vs. compare_at_price, both ÷ cans-per-pack). When the
 * price changes — Kaching Bundles tier selection, or any future source — this
 * rewrites the badge with the LIVE discount %: the selected tier's per-unit price
 * (live total ÷ the tier's total cans) compared against the same per-unit
 * compare_at_price baseline. Bigger tiers → cheaper per-unit → bigger %.
 *
 * ROBUSTNESS (2026-06-23, tweak): the previous version triggered off Kaching's
 * own widget + its `change` events and read the total from `.kaching-bundles__bar-
 * price`. That stopped following after a while — if Kaching re-renders/replaces
 * its widget the listeners die, and a tier whose bar-price fails to parse made it
 * early-return (badge froze). Now the TRIGGER + TOTAL come from the live, stable
 * `.price` inside <product-price> (a MutationObserver — the same hook that made
 * the installments line reliable, AST-009): Kaching writes the tier total there,
 * and it also covers native variants / any future price source. The DIVISOR (how
 * many cans that total is for) is still the selected Kaching tier:
 *   tier packs ("N Packs") × cans-per-pack (data-units-per-pack, the custom.unit
 *   metafield), or the tier subtitle ("N Latas"); falling back to cans-per-pack
 *   (treat as one pack) when no tier is readable — the no-Kaching server default.
 *
 * Integration layer (FRONTEND-STACK §): read-only vanilla JS. It never manages
 * Kaching's or Horizon's state.
 */
(function () {
  'use strict';

  var BADGE_SEL = '[data-switch-unit-price]';
  var WIDGET_SEL = '.kaching-bundles';
  var BAR_SEL = '.kaching-bundles__bar';

  // Shopify BRL money always renders 2 decimals → trailing 2 digits are cents.
  function parseCents(text) {
    var digits = (text || '').replace(/\D/g, '');
    return digits ? parseInt(digits, 10) : NaN;
  }

  // The active price within a scope: the first `.price` that is actually visible
  // (not the hidden regular/sale branch, not a11y-hidden).
  function visiblePrice(scope) {
    var nodes = scope.querySelectorAll('.price');
    for (var i = 0; i < nodes.length; i++) {
      var p = nodes[i];
      if (p.offsetParent !== null && !p.closest('.price__hidden, .visually-hidden, [hidden]')) {
        return p;
      }
    }
    return null;
  }

  // Live total (cents) the badge should divide — read from the <product-price>
  // element (the price Kaching rewrites), NOT the section: a section can hold a
  // second `.price` (e.g. a base/buy-box price) that Kaching never updates and
  // would otherwise win as the first visible match.
  function liveTotalCents(badge) {
    var scope = badge.closest('product-price') || document.querySelector('product-price') || document;
    var priceEl = visiblePrice(scope);
    return priceEl ? parseCents(priceEl.textContent) : NaN;
  }

  // Total cans the live total is for. Kaching represents the selected tier by
  // setting the `quantity` field = number of packs (verified live: 2-pack→qty 2,
  // 5-pack→qty 5) and writes the tier TOTAL into `.price` in the same update —
  // so packs × cans-per-pack is both correct and ATOMIC with the price. We read
  // the quantity field (not the Kaching bar title) precisely because the radio's
  // `:checked`/text lags the price update, which let the old parse compute
  // newPrice ÷ oldTierCans during a switch and freeze at an inflated per-unit.
  // Guarded by Kaching presence: without the app, `.price` is per-pack (not a
  // multi-pack total), so quantity must NOT scale the divisor — it stays
  // cans-per-pack (the qty-independent server default).
  function divisorCans(perPack) {
    var packs = 1;
    if (document.querySelector(WIDGET_SEL)) {
      var q = document.querySelector('input[name="quantity"]');
      var n = q ? parseInt(q.value, 10) : NaN;
      if (isFinite(n) && n > 0) packs = n;
    }
    return packs * (perPack > 0 ? perPack : 1);
  }

  function updateAll() {
    var badges = document.querySelectorAll(BADGE_SEL);
    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      var totalCents = liveTotalCents(badge);
      if (!isFinite(totalCents) || totalCents <= 0) continue; // no price → keep server default

      var perPack = parseInt(badge.dataset.unitsPerPack, 10) || 0;
      var cans = divisorCans(perPack);
      if (!isFinite(cans) || cans <= 0) continue;

      // Baseline: the per-unit price implied by compare_at_price ÷ the BASE pack
      // size (perPack, not the multi-pack `cans`) — the same fixed reference the
      // Liquid default uses, so the % is comparable across tiers.
      var compareAtCents = parseInt(badge.dataset.compareAtPrice, 10);
      if (!isFinite(compareAtCents) || compareAtCents <= 0 || perPack <= 0) continue; // no baseline → keep server default

      var baselinePerUnit = compareAtCents / perPack;
      var livePerUnit = totalCents / cans;
      var pct = Math.round((1 - livePerUnit / baselinePerUnit) * 100);

      // pct <= 0 is a fully-computed "no discount at this tier" result, not a
      // missing-data case — it must actively clear the badge, or picking a
      // tier with no discount after one with a discount leaves the old % stuck.
      var text = pct > 0 ? (pct + '%' + (badge.dataset.suffix ? ' ' + badge.dataset.suffix : '')) : '';
      if (badge.textContent !== text) badge.textContent = text; // idempotent → no observer loop
      badge.classList.toggle('switch-unit-price--pending', text === '');
    }
  }

  function init() {
    if (!document.querySelector(BADGE_SEL)) return;

    var raf = null;
    var schedule = function () {
      if (raf) return; // coalesce bursts
      raf = requestAnimationFrame(function () { raf = null; updateAll(); });
    };

    // Primary, robust trigger: the live price inside <product-price>. Survives
    // Kaching re-renders (product-price isn't replaced by the app) and follows
    // the price from any source.
    if (typeof MutationObserver === 'function') {
      document.querySelectorAll('product-price').forEach(function (pp) {
        new MutationObserver(schedule).observe(pp, { childList: true, characterData: true, subtree: true });
      });
    }

    // Extra triggers (belt-and-suspenders), delegated on document so they
    // survive re-renders: Kaching radio toggles, and the quantity field (its
    // value is the pack count and may settle a tick apart from .price). Covers
    // either update order — whichever of price/quantity lands last re-runs the
    // recompute, so the two are always read together-consistent.
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if ((t.name && t.name.indexOf('kaching-bundles-deal') === 0) || t.name === 'quantity') schedule();
    });
    document.addEventListener('input', function (e) {
      if (e.target && e.target.name === 'quantity') schedule();
    });

    updateAll(); // correct any pre-selected tier on load

    // Kaching renders asynchronously — re-run a few times until it has painted
    // its bars / set its default selection, then stop.
    var tries = 0;
    (function poll() {
      var widget = document.querySelector(WIDGET_SEL);
      if (widget && widget.querySelector(BAR_SEL)) { updateAll(); return; }
      if (tries++ < 40) setTimeout(poll, 250); // ~10s ceiling, then give up (no Kaching)
    })();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();